import { useLayoutEffect, useRef, type ReactNode } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useLocale } from '@/contexts/LocaleContext';
import { cn } from '@/lib/utils';

interface CarouselScrollerProps {
  children: ReactNode;
  /** Vertical position of arrows (e.g. "35%" or "40%"). */
  arrowsTop?: string;
  /** Extra classes on the scroll container. */
  className?: string;
  /** Re-normalize scroll position when this value changes. */
  resetKey?: unknown;
}

/**
 * Horizontal snap-scroll carousel with prev/next arrows and RTL-aware scrolling.
 * Shared by ActivityCarouselRow and HostsCarousel to remove duplicated
 * scroll/arrow logic.
 */
export function CarouselScroller({
  children,
  arrowsTop = '35%',
  className,
  resetKey,
}: CarouselScrollerProps) {
  const { locale } = useLocale();
  const isArabic = locale === 'ar';
  const scrollRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollLeft = 0;
  }, [isArabic, resetKey]);

  const scroll = (dir: 'prev' | 'next') => {
    const el = scrollRef.current;
    if (!el) return;
    const amount = el.clientWidth * 0.9;
    const sign = dir === 'next' ? 1 : -1;
    el.scrollBy({
      left: isArabic ? -sign * amount : sign * amount,
      behavior: 'smooth',
    });
  };

  const btnClass =
    'hidden md:flex absolute z-10 h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-background/90 backdrop-blur border border-border shadow-md hover:bg-background transition';

  return (
    <div className="relative">
      <button
        type="button"
        aria-label={isArabic ? 'السابق' : 'Previous'}
        onClick={() => scroll('prev')}
        style={{ top: arrowsTop }}
        className={cn(btnClass, 'start-0 -translate-x-1/2 rtl:translate-x-1/2')}
      >
        <ChevronLeft className="h-5 w-5 rtl:hidden" />
        <ChevronRight className="h-5 w-5 hidden rtl:block" />
      </button>
      <button
        type="button"
        aria-label={isArabic ? 'التالي' : 'Next'}
        onClick={() => scroll('next')}
        style={{ top: arrowsTop }}
        className={cn(btnClass, 'end-0 translate-x-1/2 rtl:-translate-x-1/2')}
      >
        <ChevronRight className="h-5 w-5 rtl:hidden" />
        <ChevronLeft className="h-5 w-5 hidden rtl:block" />
      </button>
      <div
        ref={scrollRef}
        className={cn(
          '-mx-4 ps-4 pe-4 overflow-x-auto snap-x scroll-smooth [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
          className
        )}
      >
        {children}
      </div>
    </div>
  );
}
